import {
  CONSOLE_CSRF_COOKIE,
  ExplorerApiError,
  type ExplorerApiErrorCode,
} from "@strust/contracts";
import type { z } from "zod";
import type { CallInput, Endpoint } from "./endpoint.ts";

/** Errors that never come from the API: network failure, malformed body, response off contract. */
export type ClientErrorCode = "network_error" | "invalid_response";

export class ApiRequestError extends Error {
  readonly status: number;
  /** Stable code: the contract error code, or a client-side one. Never translate it. */
  readonly code: ExplorerApiErrorCode | ClientErrorCode | "unknown_error";
  readonly details: ExplorerApiError["details"] | undefined;

  constructor(
    status: number,
    code: ApiRequestError["code"],
    details?: ExplorerApiError["details"],
  ) {
    super(code);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * A `400 invalid_request` that names the `environment` field: the Explorer API no longer takes the
 * parameter (design D6 of bind-operator-environment-to-installation), so only a stale bundle still
 * sends it.
 */
export function isStaleEnvironmentRequest(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    error.status === 400 &&
    error.code === "invalid_request" &&
    (error.details?.fields ?? []).includes("environment")
  );
}

export type CsvOutcome<T> =
  | { readonly kind: "csv"; readonly blob: Blob; readonly filename: string }
  | { readonly kind: "json"; readonly data: T };

export interface ApiClientOptions {
  /** Same origin as the SPA; the dev server proxies it to the local `explorer` role. */
  readonly baseUrl?: string;
  readonly fetch?: typeof globalThis.fetch;
  /** Called on `unauthenticated` (session ended or missing): the app sends the user to login. */
  readonly onUnauthorized?: () => void;
  /**
   * Called on a `400 invalid_request` that names the `environment` field: the API no longer takes
   * it (design D6 of bind-operator-environment-to-installation), so only a stale bundle sends it;
   * the app reloads to fetch the current one.
   */
  readonly onStaleBundle?: () => void;
}

export interface ApiClient {
  call: <E extends Endpoint>(endpoint: E, input: CallInput<E>) => Promise<z.output<E["response"]>>;
  /**
   * Like `call` for an operation that may answer a `text/csv` attachment instead of its JSON body
   * (`POST /exports` up to 10,000 rows): the CSV comes back as a Blob with its file name.
   */
  callCsv: <E extends Endpoint>(
    endpoint: E,
    input: CallInput<E>,
  ) => Promise<CsvOutcome<z.output<E["response"]>>>;
  /**
   * The synchronizer token that the login/session response carries; sent on unsafe methods. After
   * a reload or an SSO redirect the JS-readable `__Host-strust_csrf` cookie is used instead.
   */
  setCsrfToken: (token: string | undefined) => void;
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? "/explorer/v1";
  const doFetch = options.fetch ?? ((...args) => globalThis.fetch(...args));
  let csrfToken: string | undefined;

  const send = async <E extends Endpoint>(
    endpoint: E,
    input: CallInput<E>,
    accept: string,
  ): Promise<Response> => {
    const url = buildUrl(baseUrl, endpoint.path, input.params, input.query);
    const headers: Record<string, string> = { Accept: accept };
    let body: string | undefined;
    if (endpoint.body) {
      body = JSON.stringify(endpoint.body.parse(input.body));
      headers["Content-Type"] = "application/json";
    }
    const csrf = csrfToken ?? readCsrfCookie();
    if (UNSAFE_METHODS.has(endpoint.method) && csrf) headers["X-CSRF-Token"] = csrf;

    try {
      return await doFetch(url, {
        method: endpoint.method,
        headers,
        ...(body === undefined ? {} : { body }),
        ...(input.signal ? { signal: input.signal } : {}),
        credentials: "same-origin",
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new ApiRequestError(0, "network_error");
    }
  };

  const parseJson = async <E extends Endpoint>(
    endpoint: E,
    response: Response,
  ): Promise<z.output<E["response"]>> => {
    const payload = await readJson(response);
    if (!response.ok) {
      const parsed = ExplorerApiError.safeParse(payload);
      // Only a missing or ended session sends the user to login; `invalid_credentials` is a refusal.
      if (parsed.success && parsed.data.error === "unauthenticated") options.onUnauthorized?.();
      const error = new ApiRequestError(
        response.status,
        parsed.success ? parsed.data.error : "unknown_error",
        parsed.success ? parsed.data.details : undefined,
      );
      if (isStaleEnvironmentRequest(error)) options.onStaleBundle?.();
      throw error;
    }
    const parsed = endpoint.response.safeParse(payload);
    if (!parsed.success) throw new ApiRequestError(response.status, "invalid_response");
    // The payload was just validated against the endpoint's own schema, so the cast is sound.
    return parsed.data as z.output<E["response"]>;
  };

  const call = async <E extends Endpoint>(
    endpoint: E,
    input: CallInput<E>,
  ): Promise<z.output<E["response"]>> =>
    parseJson(endpoint, await send(endpoint, input, "application/json"));

  const callCsv = async <E extends Endpoint>(
    endpoint: E,
    input: CallInput<E>,
  ): Promise<CsvOutcome<z.output<E["response"]>>> => {
    const response = await send(endpoint, input, "text/csv, application/json");
    const type = response.headers.get("Content-Type") ?? "";
    if (response.ok && type.startsWith("text/csv")) {
      const blob = await response.blob();
      return { kind: "csv", blob, filename: csvFilename(response.headers) };
    }
    return { kind: "json", data: await parseJson(endpoint, response) };
  };

  return {
    call,
    callCsv,
    setCsrfToken: (token) => {
      csrfToken = token;
    },
  };
}

/** The attachment name the API chose; a safe default when the header is missing or odd. */
function csvFilename(headers: Headers): string {
  const match = /filename="?([\w.-]+)"?/.exec(headers.get("Content-Disposition") ?? "");
  return match?.[1] ?? "export.csv";
}

function readCsrfCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = `${CONSOLE_CSRF_COOKIE}=`;
  const entry = document.cookie.split("; ").find((part) => part.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : undefined;
}

async function readJson(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

export function buildUrl(
  baseUrl: string,
  path: string,
  params: object | undefined,
  query: unknown,
): string {
  const resolved = path.replace(/:(\w+)/g, (_, name: string) => {
    const value: unknown = params ? Reflect.get(params, name) : undefined;
    if (typeof value !== "string") throw new Error(`missing path parameter "${name}"`);
    return encodeURIComponent(value);
  });
  const search = new URLSearchParams();
  if (typeof query === "object" && query !== null) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      // Repeatable filters (`operator_id`, `reason`, `id`) travel as repeated params.
      if (Array.isArray(value)) {
        for (const item of value) search.append(key, String(item));
        continue;
      }
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return `${baseUrl}${resolved}${qs ? `?${qs}` : ""}`;
}
